import { animateItemsIn } from './modules/animate-items-in.js';
import { watchPreviewRoot } from './modules/editor-preview-root.js';
import { getResponsiveDevice } from './modules/viewport-device.js';

/**
 * JCTC Smart Rows AJAX Logic (Optimized for resources)
 */
export const initEventsAjax = () => {
    // The front end renders exactly one grid/button pair in the initial
    // HTML, so binding once and stopping is enough there. The block editor
    // loads this same script for a same-as-frontend preview, but ACF's
    // ServerSideRender replaces the block's whole preview markup (a brand
    // new .js-events-grid/.js-load-events pair) on every attribute or
    // edit/preview-mode change -- same behavior initEventsPreviewScale's own
    // doc comment above already establishes for its own target elements.
    // Disconnecting after the first match left every later re-render's
    // "Load more" button and resize handling permanently unbound until a
    // full page reload; this instead keeps watching for the page's lifetime
    // and tracks which grid/button pairs are already bound so a re-render
    // gets rebound instead of silently ignored.
    const bound = new WeakSet();

    const bindIfNew = () => {
        const grid = document.querySelector('.js-events-grid');
        const loadBtn = document.querySelector('.js-load-events');
        if (grid && loadBtn && !bound.has(grid)) {
            bound.add(grid);
            bindEventsGrid(grid, loadBtn);
        }
    };

    bindIfNew();

    const observer = new MutationObserver(bindIfNew);
    observer.observe(document.body, { childList: true, subtree: true });
};

/**
 * Block-editor-preview-only: scales every matching block's container down to
 * fit its actual available width, instead of the desktop-fixed size
 * overflowing into a horizontal scrollbar. Uses `zoom` (not
 * `transform: scale`) because zoom reflows layout at the scaled size --
 * transform would leave the element's full unscaled height/width as empty
 * space in the document flow.
 *
 * The target elements only exist in the block editor's own markup
 * (.js-editor-preview-scale, added by blocks/upcoming-events/render.php,
 * blocks/recommended-events/render.php and template-parts/
 * event-carousel-section.php only when $is_preview/$is_admin_preview is
 * true) so this never runs, and never affects anything, on the live front
 * end.
 *
 * Those elements live inside Gutenberg's iframed editor canvas
 * (iframe[name="editor-canvas"]) -- a document this script itself never
 * loads into (see modules/editor-preview-root.js's own doc comment for why:
 * WordPress duplicates enqueued STYLES into that iframe, never SCRIPTS).
 * watchPreviewRoot() resolves the right document to scan/observe -- the
 * iframe's when one exists, the outer `document` otherwise -- confirmed
 * live as the actual reason this scaling never fired: without it,
 * `document.querySelectorAll(SELECTOR)` always ran against the outer
 * document, where these elements never exist, silently finding nothing.
 *
 * Both blocks can be on the same page at once, each needing its own
 * independent scale factor (their available column widths aren't
 * necessarily equal), so this tracks a live Set of attached elements/
 * observers instead of a single current one.
 */
export const initEventsPreviewScale = () => {
    const REFERENCE_WIDTH = 1440;
    const SELECTOR = '.js-editor-preview-scale';
    const attached = new Map(); // el -> ResizeObserver

    const scaleToFit = (el) => {
        const parent = el.parentElement;
        if (!parent) return;
        const available = parent.clientWidth;
        if (!available) return;
        const zoom = Math.min(1, available / REFERENCE_WIDTH);
        el.style.zoom = zoom;
    };

    const attach = (el) => {
        if (attached.has(el)) return;
        scaleToFit(el);
        const parent = el.parentElement;
        if (!parent || typeof ResizeObserver === 'undefined') {
            attached.set(el, null);
            return;
        }
        const resizeObserver = new ResizeObserver(() => scaleToFit(el));
        resizeObserver.observe(parent);
        attached.set(el, resizeObserver);
    };

    const attachAll = (root) => {
        // Prunes entries for elements a since-replaced ACF re-render already
        // removed from the DOM -- confirmed (in the field, across three
        // sibling modules with this exact same shape: recommended-glow-canvas.js/
        // recommended-glow-viewport.js/swiper-viewport-autoplay.js) as a
        // real, unbounded leak rather than the harmless situation an earlier
        // version of this doc comment assumed: ResizeObserver keeps a
        // strong reference to whatever it's observing for as long as that
        // observation stays active, so a detached parent -- and its whole
        // old subtree -- stays reachable and un-garbage-collectable for the
        // rest of the page's life without this. Every attribute-driven
        // re-render (a drag reorder, the admin panel's own live-preview
        // refresh, toggling "Recommended") adds one more, permanently.
        attached.forEach((resizeObserver, el) => {
            if (el.isConnected) {
                return;
            }
            // disconnect() alone fully tears down the observer regardless of
            // target -- a separate unobserve(el) call here would be a no-op
            // anyway, since what was actually observed was el's (now-null)
            // parentElement, never el itself.
            resizeObserver?.disconnect();
            attached.delete(el);
        });
        root.querySelectorAll(SELECTOR).forEach(attach);
    };

    // ACF's ServerSideRender replaces each of these elements with a brand
    // new DOM node (same class, different element) whenever its block's
    // attributes change or its edit/preview mode toggles -- e.g. entering
    // block-edit mode and leaving it again -- which detaches whatever
    // `attach()` was watching and leaves the replacement unscaled/
    // overflowing. So this keeps watching for the lifetime of the page
    // (never disconnects), re-attaching to any newly-appeared element
    // instead of stopping after the first batch found. Stale entries are
    // pruned above instead, not left to accumulate.
    watchPreviewRoot((root) => {
        attachAll(root);
        const observer = new MutationObserver(() => attachAll(root));
        observer.observe(root.body, { childList: true, subtree: true });
    });
};

// When the grid's own item count isn't an exact multiple of the current
// column count, CSS Grid just left-aligns the leftover row's items into
// however many of the row's own track columns they land in (event-card.php
// items place left-to-right through the grid's explicit column tracks,
// leaving any unused trailing tracks on that row empty) -- there's no pure-
// CSS way to instead center a single leftover card, or space 2+ of them
// apart, across a CSS Grid row's own tracks. So, ONLY when
// data-last-row-centered="1" (blocks/upcoming-events/render.php's own
// "Center Incomplete Last Row" admin toggle -- off/left-to-right by
// default), the leftover cards (however many of them there are, 1 through
// cols-1) get pulled out of the grid flow into their own flex sub-row
// spanning the same full width a normal row would (grid-column: 1 / -1 in
// _events.scss), where flexbox's own justify-content DOES have exactly this
// "center one item / space several apart" behavior built in -- see that
// CSS's own :has() selector for how it picks center vs. space-between.
const LAST_ROW_WRAPPER_CLASS = 'js-events-grid-last-row';

const unwrapEventsGridLastRow = (grid) => {
    const wrapper = grid.querySelector(`:scope > .${LAST_ROW_WRAPPER_CLASS}`);
    if (!wrapper) return;
    while (wrapper.firstChild) {
        grid.insertBefore(wrapper.firstChild, wrapper);
    }
    wrapper.remove();
};

const applyEventsGridLastRow = (grid, cols) => {
    unwrapEventsGridLastRow(grid);

    if (grid.dataset.lastRowCentered !== '1') {
        return;
    }

    // Only cards actually on screen count toward "how many fill the last
    // row" -- adjustInitialView()/the load-more reveal step both hide extra
    // pre-rendered cards via display:none rather than removing them, and
    // those must not be mistaken for part of the visible grid here.
    const visibleCards = Array.from(grid.querySelectorAll(':scope > .event-card')).filter(
        (card) => card.style.display !== 'none'
    );
    if (!visibleCards.length || cols <= 1) return;

    const remainder = visibleCards.length % cols;
    if (remainder === 0) return;

    const leftoverCards = visibleCards.slice(visibleCards.length - remainder);
    const wrapper = document.createElement('div');
    wrapper.className = LAST_ROW_WRAPPER_CLASS;
    leftoverCards.forEach((card) => {
        wrapper.appendChild(card);
    });
    grid.appendChild(wrapper);
};

function bindEventsGrid(grid, loadBtn) {
    const setLoadButtonVisible = (isVisible) => {
        loadBtn.hidden = !isVisible;
    };

    // .event-card--test marks a "Add test cards" preview card (real recent
    // past event or synthetic placeholder -- see blocks/upcoming-events/
    // render.php's own "Test-card display logic" doc comment). loadBtn's
    // own data-total (jctc_count_unique_upcoming_event_posts()) counts only
    // real upcoming events, so any offset/total comparison sent to or
    // checked against load_more_events must exclude test cards too, or a
    // logged-in editor previewing test cards silently skips/hides real
    // upcoming events on "Load more".
    const getRealCardCount = () =>
        grid.querySelectorAll('.event-card:not(.event-card--test)').length;

    const getColsCount = () => {
        const gridStyle = window.getComputedStyle(grid);
        const cols = gridStyle.getPropertyValue('grid-template-columns').split(' ').length;
        return cols || 1;
    };

    // blocks/upcoming-events/render.php sets data-rows-{desktop,tablet,mobile}
    // from its own Desktop/Tablet/Mobile Rows fields; falls back to this
    // grid's previous fixed values when absent (e.g. the flexible-content
    // layout's own events-grid, section-templates/upcoming_events_section.php,
    // which has no such fields).
    const FALLBACK_ROWS = { desktop: 2, tablet: 3, mobile: 3 };
    const getRowsToKeep = () => {
        const device = getResponsiveDevice();
        const fromData = parseInt(
            grid.dataset[`rows${device[0].toUpperCase()}${device.slice(1)}`] || '',
            10
        );
        return Number.isFinite(fromData) && fromData > 0 ? fromData : FALLBACK_ROWS[device];
    };

    // Cards explicitly revealed via a "Load more" click (either by unhiding
    // pre-rendered cards or by inserting freshly-fetched ones) must never be
    // re-hidden by a later adjustInitialView() call -- resize is also fired
    // by mobile browsers auto-hiding/showing the URL bar while the page
    // scrolls (e.g. right after a "Load more" tap scrolls the grid), so
    // without this floor that alone silently collapsed the grid back to its
    // initial row count a second or two after the user had already expanded
    // it. Written ONLY by the click handler below (branches 1/2), never by
    // adjustInitialView() itself -- it used to also assign this on every one
    // of its own ordinary calls (including the automatic resize handler at
    // the bottom of this function, and the very first call on page load),
    // which meant simply viewing the grid at a wide viewport (more columns,
    // more cards naturally shown) silently raised this floor with no Load
    // More click involved. Confirmed live: widen to tablet width (2 cols x 3
    // rows = 6 shown) then narrow back to phone width (1 col x 3 rows = 3
    // "should" show) -- the count stayed stuck at 6, since resize's own
    // adjustInitialView() call had already baked 6 in as the floor.
    let revealedCount = 0;

    // Trim initial cards to match rows limit
    const adjustInitialView = () => {
        const cols = getColsCount();
        const rowsToKeep = getRowsToKeep();
        const totalToKeep = cols * rowsToKeep;
        const keepCount = Math.max(totalToKeep, revealedCount);

        const allCards = grid.querySelectorAll('.event-card');
        allCards.forEach((card, index) => {
            card.style.display = index < keepCount ? '' : 'none';
        });

        applyEventsGridLastRow(grid, cols);

        // Keep the affordance when the first view is exactly full; hide only when
        // there is visibly less content than one configured load step.
        const totalEvents = parseInt(loadBtn.dataset.total || '0', 10);
        if (allCards.length <= totalToKeep && allCards.length >= totalEvents) {
            setLoadButtonVisible(false);
            return;
        }

        setLoadButtonVisible(true);
    };

    loadBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        // The 'loading' CSS class alone (pointer-events: none, set below
        // once branch 2 actually fetches) doesn't block a SECOND click from
        // firing before the first fetch resolves -- CSS pointer-events only
        // gates pointer/mouse hit-testing, not a keyboard Enter/Space
        // activation on the already-focused button. A held key or a fast
        // repeat keypress can fire this handler twice with the same
        // `offset` (computed synchronously below, before the await), which
        // would double-fetch and append the same page of events twice.
        // loadBtn.disabled is the actual native way to block ALL activation
        // paths, not just pointer ones.
        if (loadBtn.disabled) {
            return;
        }
        loadBtn.disabled = true;

        // Wraps the WHOLE handler body (both branches, every early return
        // included) -- disabled has to come back off regardless of which
        // path this run takes, not just the fetch branch's own try/finally
        // (which never covered branch 1's returns at all).
        try {
            const cols = getColsCount();
            const device = getResponsiveDevice();
            const rowsToAdd = getRowsToKeep();
            const totalNeeded = cols * rowsToAdd;

            // 1. Check if we have hidden cards from initial load to reveal
            const hiddenCards = grid.querySelectorAll('.event-card[style*="display: none"]');
            if (hiddenCards.length > 0) {
                const revealedCards = [];
                hiddenCards.forEach((card, index) => {
                    if (index < totalNeeded) {
                        card.style.display = '';
                        revealedCards.push(card);
                    }
                });
                animateItemsIn(revealedCards);
                revealedCount += revealedCards.length;

                applyEventsGridLastRow(grid, cols);

                const remainingHiddenCards = grid.querySelectorAll(
                    '.event-card[style*="display: none"]'
                );
                if (remainingHiddenCards.length > 0) {
                    return;
                }

                if (getRealCardCount() >= parseInt(loadBtn.dataset.total || '0', 10)) {
                    setLoadButtonVisible(false);
                    return;
                }

                return;
            }

            // 2. Fetch more from server if needed
            const offset = getRealCardCount();
            const totalEvents = parseInt(loadBtn.dataset.total || '0', 10);
            if (offset >= totalEvents) {
                setLoadButtonVisible(false);
                return;
            }

            loadBtn.classList.add('loading');
            const formData = new FormData();
            formData.append('action', 'load_more_events');
            formData.append('offset', offset);
            formData.append('cols', cols);
            formData.append('rows', rowsToAdd);
            formData.append('device', device);
            // blocks/upcoming-events/render.php sets data-group-series from
            // its own "Ungroup Single-Day Sibling Events" Advanced field;
            // falls back to '1' (grouped -- current behavior) when absent,
            // same convention as data-rows-{desktop,tablet,mobile} above.
            formData.append('group_series', grid.dataset.groupSeries === '0' ? '0' : '1');
            formData.append('nonce', window.jctc_vars?.nonce || '');

            try {
                const response = await fetch(window.jctc_vars.ajax_url, {
                    method: 'POST',
                    body: formData,
                });
                const result = await response.json();

                if (result.success) {
                    if (result.data.html) {
                        // Counted BEFORE insertion so only the newly-added cards
                        // (not every card already on screen) get the enter
                        // animation -- insertAdjacentHTML gives back no reference
                        // to what it just parsed in, so this is the plain way to
                        // isolate them afterward.
                        const countBefore = grid.querySelectorAll('.event-card').length;
                        grid.insertAdjacentHTML('beforeend', result.data.html);
                        animateItemsIn(
                            Array.from(grid.querySelectorAll('.event-card')).slice(countBefore)
                        );
                        revealedCount = grid.querySelectorAll('.event-card').length;
                    }

                    loadBtn.dataset.total =
                        result.data.total_events || loadBtn.dataset.total || '0';
                    applyEventsGridLastRow(grid, cols);

                    if (getRealCardCount() >= parseInt(loadBtn.dataset.total || '0', 10)) {
                        setLoadButtonVisible(false);
                    }
                }
            } catch (error) {
                console.error('AJAX Load Error:', error);
            } finally {
                loadBtn.classList.remove('loading');
            }
        } finally {
            loadBtn.disabled = false;
        }
    });

    // Run adjustment on load and resize.
    //
    // User-reported regression (2026-09-05): "Load more" sometimes revealed
    // events that should have already been visible on first load -- not
    // every reload, only some (2nd/3rd/4th attempt). Root cause: this
    // theme's own compiled stylesheet (`jctc-style`) loads asynchronously
    // since JCTCTAS-119 (`media="print" onload="this.media='all'"` swap,
    // inc/scripts.php) -- a completely separate, uncoordinated mechanism
    // from this module's own defer timing (JCTCTAS-99). getColsCount()
    // above reads `getComputedStyle(grid)`'s `grid-template-columns`, which
    // is only correct once `.events-grid`'s real CSS (blocks/_events.scss,
    // part of `jctc-style`) has actually applied -- if this module's own
    // script executes first, the browser's pre-CSS default
    // (`grid-template-columns: none`) makes `getColsCount()` return 1,
    // silently under-counting how many cards should stay visible. The
    // `<link id="jctc-style-css">` id is WP core's own stable
    // `{handle}-css` convention, safe to rely on. Only the FIRST call needs
    // this guard -- by the time a real resize fires, the stylesheet has
    // long since applied either way.
    const jctcStyleLink = document.getElementById('jctc-style-css');
    if (!jctcStyleLink || jctcStyleLink.media === 'all') {
        adjustInitialView();
    } else {
        jctcStyleLink.addEventListener('load', adjustInitialView, { once: true });
    }
    let resizeTimer;
    const onResize = () => {
        // initEventsAjax() now rebinds to each new ACF-editor re-render
        // instead of stopping after the first (see its own doc comment) --
        // an older, now-detached grid's listener has nothing left to do, so
        // it removes itself on the next resize instead of accumulating one
        // permanently-dead listener per re-render for the rest of the page's
        // life.
        if (!grid.isConnected) {
            window.removeEventListener('resize', onResize);
            return;
        }
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(adjustInitialView, 200);
    };
    window.addEventListener('resize', onResize);
}
